import { useQuery, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useState } from 'react';
import { Link, useParams } from 'react-router';
import { Alert, Badge, Button, Card, Checkbox, Field, Select, Spinner, Textarea } from '../../components/ui';
import { type MessageKey, t } from '../../i18n';
import { api } from '../../lib/api';
import { useMe } from '../../lib/auth';
import { errorMessage } from '../../lib/errors';
import { CHANGE_KINDS, canSeeAll, formatDate, isHr, type PersonDetail, type PersonHistoryEntry, usePeopleForm } from './data';
import { PeopleNav } from './PeopleNav';
import { PersonLicenses } from './PersonLicenses';

const STATUS_TONES = { activo: 'blue', inactivo: 'amber', baja: 'slate' } as const;

/**
 * Ficha de una persona (guía 7.5): sus datos, licencias e historial. RR. HH. e IT editan los datos; quien gestiona
 * licencias (o administra la plataforma) asigna y libera; su jefe directo pide los cambios (crea un ticket para IT).
 */
export function PersonPage() {
  const { id = '' } = useParams();
  const me = useMe();
  const person = useQuery({ queryKey: ['usuarios', 'persona', id], queryFn: () => api<PersonDetail>(`/api/usuarios/${id}`) });
  const [editing, setEditing] = useState(false);
  const [notice, setNotice] = useState<{ tone: 'success' | 'warning'; text: string } | null>(null);

  if (!person.data) return person.error ? <Alert>{errorMessage(person.error)}</Alert> : <Spinner label={t('comun.cargando')} />;
  const p = person.data;

  return (
    <div className="space-y-4">
      <div>
        <Link to={canSeeAll(me) ? '/usuarios/directorio' : '/usuarios/equipo'} className="text-sm text-marca hover:underline">
          ← {t(canSeeAll(me) ? 'us.directorio' : 'us.equipo')}
        </Link>
        <h1 className="mt-1 text-2xl font-semibold text-tinta">
          {p.fullName}
          {p.status !== 'activo' && (
            <span className="ml-2 align-middle">
              <Badge tone={STATUS_TONES[p.status]}>{t(`us.usuario_${p.status}` as MessageKey)}</Badge>
            </span>
          )}
        </h1>
        <p className="text-sm text-slate-600">
          {p.email} · {p.organization}
        </p>
      </div>
      <PeopleNav />
      {notice && <Alert tone={notice.tone}>{notice.text}</Alert>}

      <div className="grid gap-4 lg:grid-cols-[1fr_22rem]">
        <div className="space-y-4">
          {editing ? (
            <EditCard
              person={p}
              onDone={(text, tone) => {
                setEditing(false);
                setNotice(text ? { tone, text } : null);
              }}
            />
          ) : (
            <Card title={t('us.ficha_datos')}>
              <dl className="grid gap-x-6 gap-y-3 text-sm sm:grid-cols-2">
                <Item label={t('us.ficha_organizacion')} value={p.organization} />
                <Item label={t('us.campo_departamento')} value={p.department} />
                <Item label={t('us.campo_cargo')} value={p.position} />
                <Item label={t('us.campo_jefe')} value={p.manager} />
                <Item label={t('us.campo_correo')} value={p.email} />
                <Item label={t('us.ficha_vp')} value={t(p.isVp ? 'us.si' : 'us.no')} />
              </dl>
              <p className="mt-3 text-xs text-slate-500">{t('us.ficha_alta', { fecha: formatDate(p.createdAt) })}</p>
              {p.can.edit && (
                <Button
                  variant="secondary"
                  className="mt-4"
                  onClick={() => {
                    setNotice(null);
                    setEditing(true);
                  }}
                >
                  {t('us.ficha_editar')}
                </Button>
              )}
            </Card>
          )}
          <PersonLicenses person={p} onDone={(text) => setNotice({ tone: 'success', text })} />
        </div>
        <div className="space-y-4">
          {p.can.request && <ChangeRequestCard person={p} />}
          {(isHr(me, p.organizationId) || p.history.length > 0) && <HistoryCard entries={p.history} />}
        </div>
      </div>
    </div>
  );
}

function Item({ label, value }: { label: string; value: string | null }) {
  return (
    <div>
      <dt className="text-xs text-slate-500">{label}</dt>
      <dd className="text-slate-900">{value ?? '—'}</dd>
    </div>
  );
}

/**
 * Edición (RR. HH. e IT): envía solo lo que cambió. Al cambiar de organización, el departamento y el jefe se eligen
 * de nuevo (son de cada organización) y se ofrecen solo las organizaciones donde quien edita puede editar.
 */
function EditCard({ person: p, onDone }: { person: PersonDetail; onDone: (text: string | null, tone: 'success' | 'warning') => void }) {
  const me = useMe();
  const qc = useQueryClient();
  const [organizationId, setOrganizationId] = useState(p.organizationId);
  const form = usePeopleForm(organizationId);
  const [fullName, setFullName] = useState(p.fullName);
  const [email, setEmail] = useState(p.email);
  const [departmentId, setDepartmentId] = useState(p.departmentId ?? '');
  const [positionId, setPositionId] = useState(p.positionId ?? '');
  const [managerId, setManagerId] = useState(p.managerId ?? '');
  const [isVp, setIsVp] = useState(p.isVp);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!form.data) return form.error ? <Alert>{errorMessage(form.error)}</Alert> : <Spinner label={t('comun.cargando')} />;
  const data = form.data;
  const moving = organizationId !== p.organizationId;
  const organizations = data.organizations.filter((o) => o.id === p.organizationId || isHr(me, o.id));
  const managers = data.managers.filter((m) => m.id !== p.id);

  function changeOrganization(id: string) {
    setOrganizationId(id);
    setDepartmentId(id === p.organizationId ? (p.departmentId ?? '') : '');
    setManagerId(id === p.organizationId ? (p.managerId ?? '') : '');
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    const body: Record<string, unknown> = {};
    if (fullName.trim() !== p.fullName) body.fullName = fullName.trim();
    if (email.trim().toLowerCase() !== p.email.toLowerCase()) body.email = email.trim();
    if (moving) body.organizationId = organizationId;
    if (moving || (departmentId || null) !== p.departmentId) body.departmentId = departmentId || null;
    if ((positionId || null) !== p.positionId) body.positionId = positionId || null;
    if (managerId && (moving || managerId !== p.managerId)) body.managerId = managerId;
    if (isVp !== p.isVp) body.isVp = isVp;
    if (Object.keys(body).length === 0) return onDone(t('us.editado_sin_cambios'), 'success');
    setBusy(true);
    setError(null);
    try {
      const res = await api<{ changed: string[]; moved: boolean }>(`/api/usuarios/${p.id}`, { method: 'PATCH', json: body });
      await qc.invalidateQueries({ queryKey: ['usuarios'] });
      onDone(res.moved ? t('us.editado_movido') : res.changed.length ? t('us.editado_ok') : t('us.editado_sin_cambios'), res.moved ? 'warning' : 'success');
    } catch (err) {
      setError(errorMessage(err));
      setBusy(false);
    }
  }

  return (
    <Card title={t('us.ficha_editar')}>
      <form onSubmit={submit} className="space-y-4">
        <p className="text-sm text-slate-600">{t('us.editar_ayuda')}</p>
        {error && <Alert>{error}</Alert>}
        <Field id="us-ed-nombre" label={t('us.campo_nombre_completo')} required maxLength={200} value={fullName} onChange={(e) => setFullName(e.target.value)} />
        <Field id="us-ed-correo" type="email" label={t('us.campo_correo')} required maxLength={254} value={email} onChange={(e) => setEmail(e.target.value)} />
        {organizations.length > 1 && (
          <Select id="us-ed-org" label={t('us.ficha_organizacion')} value={organizationId} onChange={(e) => changeOrganization(e.target.value)}>
            {organizations.map((o) => (
              <option key={o.id} value={o.id}>
                {o.groupName} › {o.name}
              </option>
            ))}
          </Select>
        )}
        {moving && <Alert tone="info">{t('us.editar_org_ayuda')}</Alert>}
        <div className="grid gap-4 sm:grid-cols-2">
          <Select id="us-ed-depto" label={t('us.campo_departamento')} value={departmentId} onChange={(e) => setDepartmentId(e.target.value)}>
            <option value="">{t('us.sin_elegir')}</option>
            {data.departments.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </Select>
          <Select id="us-ed-cargo" label={t('us.campo_cargo')} value={positionId} onChange={(e) => setPositionId(e.target.value)}>
            <option value="">{t('us.sin_elegir')}</option>
            {/* Un cargo desactivado que ya tenía se sigue mostrando. */}
            {p.positionId && !data.positions.some((x) => x.id === p.positionId) && <option value={p.positionId}>{p.position}</option>}
            {data.positions.map((x) => (
              <option key={x.id} value={x.id}>
                {x.name}
              </option>
            ))}
          </Select>
        </div>
        <Select id="us-ed-jefe" label={t('us.campo_jefe')} required={moving || !!p.managerId} value={managerId} onChange={(e) => setManagerId(e.target.value)}>
          <option value="">{t('us.elegir_jefe')}</option>
          {managerId && !managers.some((m) => m.id === managerId) && <option value={managerId}>{p.manager}</option>}
          {managers.map((m) => (
            <option key={m.id} value={m.id}>
              {m.fullName}
            </option>
          ))}
        </Select>
        <Checkbox checked={isVp} onChange={(e) => setIsVp(e.target.checked)} label={t('us.ficha_vp')} />
        <div className="flex gap-2">
          <Button type="submit" disabled={busy || !fullName.trim() || !email.trim() || (moving && !managerId)}>
            {t('comun.guardar')}
          </Button>
          <Button variant="secondary" onClick={() => onDone(null, 'success')}>
            {t('comun.cancelar')}
          </Button>
        </div>
      </form>
    </Card>
  );
}

/** El jefe directo pide el cambio: se crea un ticket para IT y RR. HH. recibe el aviso. */
function ChangeRequestCard({ person: p }: { person: PersonDetail }) {
  const qc = useQueryClient();
  const [kinds, setKinds] = useState<string[]>([]);
  const [detail, setDetail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<{ number: number; code: string } | null>(null);

  const toggle = (k: string) => setKinds((ks) => (ks.includes(k) ? ks.filter((x) => x !== k) : [...ks, k]));

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      setCreated(await api<{ number: number; code: string }>(`/api/usuarios/${p.id}/cambio`, { json: { kinds, detail: detail.trim() } }));
      setKinds([]);
      setDetail('');
      await qc.invalidateQueries({ queryKey: ['tickets'] });
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card title={t('us.cambio_titulo')}>
      <form onSubmit={submit} className="space-y-3">
        <p className="text-sm text-slate-600">{t('us.cambio_ayuda')}</p>
        {created && (
          <Alert tone="success">
            {t('us.cambio_ok')}{' '}
            <Link to={`/tickets/${created.number}`} className="font-medium underline">
              {created.code}
            </Link>
          </Alert>
        )}
        {error && <Alert>{error}</Alert>}
        <fieldset className="space-y-2">
          <legend className="text-sm font-medium text-slate-700">{t('us.cambio_que')}</legend>
          {CHANGE_KINDS.map((k) => (
            <Checkbox key={k} checked={kinds.includes(k)} onChange={() => toggle(k)} label={t(`us.cambio_${k}` as MessageKey)} />
          ))}
        </fieldset>
        <Textarea id="us-cambio-detalle" label={t('us.cambio_detalle')} placeholder={t('us.cambio_detalle_ayuda')} rows={4} maxLength={2_000} value={detail} onChange={(e) => setDetail(e.target.value)} />
        <Button type="submit" disabled={busy || kinds.length === 0 || detail.trim().length < 5}>
          {t('us.cambio_enviar')}
        </Button>
      </form>
    </Card>
  );
}

const HISTORY_ACTIONS: Record<string, MessageKey> = {
  'usuarios.actualizado': 'us.historial_actualizado',
  'usuarios.cambio.solicitado': 'us.historial_cambio',
  'usuarios.reset_mfa.solicitado': 'us.historial_reset_mfa',
  'usuarios.reset_clave.solicitado': 'us.historial_reset_clave',
};

const shown = (v: unknown) => (v === null || v === undefined || v === '' ? '—' : typeof v === 'boolean' ? t(v ? 'us.si' : 'us.no') : String(v));

/** Quién cambió qué dato, de qué valor a cuál y cuándo (de la auditoría). */
function HistoryCard({ entries }: { entries: PersonHistoryEntry[] }) {
  return (
    <Card title={t('us.ficha_historial')}>
      {entries.length === 0 ? (
        <p className="text-sm text-slate-500">{t('us.historial_vacio')}</p>
      ) : (
        <ol className="space-y-3 text-sm">
          {entries.map((h) => (
            <li key={h.id} className="border-l-2 border-slate-200 pl-3">
              <p className="text-slate-900">
                <span className="font-medium">{h.actor ?? '—'}</span> · {t(HISTORY_ACTIONS[h.action] ?? ('us.historial_actualizado' as MessageKey))}
              </p>
              <p className="text-xs text-slate-500">{new Date(h.at).toLocaleString('es-PA', { dateStyle: 'medium', timeStyle: 'short' })}</p>
              {h.action === 'usuarios.actualizado' ? (
                <ul className="mt-1 space-y-0.5 text-slate-700">
                  {Object.keys(h.after).map((f) => (
                    <li key={f}>
                      {t(`us.campo_${f}` as MessageKey)}: <span className="text-slate-500 line-through">{shown(h.before[f])}</span> → {shown(h.after[f])}
                    </li>
                  ))}
                </ul>
              ) : (
                !!(h.after.cambios || h.after.ticket) && (
                  <p className="mt-1 text-slate-700">
                    {[h.after.cambios, h.after.ticket].filter(Boolean).map(String).join(' · ')}
                    {typeof h.after.detalle === 'string' && <span className="block text-slate-600">{h.after.detalle}</span>}
                  </p>
                )
              )}
            </li>
          ))}
        </ol>
      )}
    </Card>
  );
}
